import React, { useState, useEffect, ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import apiHelper from '../../api/ApiHelper';
import './ManageImages.css';
import { Image, ImageType } from '../../components/types/ArticleTypes';

const ManageImages: React.FC = () => {
    const { articleId } = useParams<{ articleId: string }>();
    const [images, setImages] = useState<Image[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadStatus, setUploadStatus] = useState<string>('');
    const [image, setImage] = useState<Image>({
        imageUrl: '',
        type: ImageType.ArticleImage, // Default value
        title: '',
        content: '',
        source: '',
        credit: ''
    });

    const mapImageType = (type: number): ImageType => {
        switch (type) {
            case 0:
                return ImageType.MainImage;
            case 1:
                return ImageType.ArticleImage;
            case 2:
                return ImageType.SlideShowImage;
            default:
                return ImageType.ArticleImage; // Default value
        }
    };

    useEffect(() => {
        const fetchImages = async () => {
            try {
                const data = await apiHelper.fetchArticleImages(articleId!);
                const mappedImages = data.images.map((img: any) => ({
                    ...img,
                    type: mapImageType(img.type)
                }));
                setImages(mappedImages);
            } catch (error) {
                alert('Failed to fetch images.');
            }
        };

        fetchImages();
    }, [articleId]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        setImage(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleUpload = async () => {
        if (image.imageUrl !== '') {//update
            await apiHelper.updateArticleImageData(articleId!, image);
            refreshPage();
            return;
        }

        if (!selectedFile) {
            setUploadStatus('Please select a file first.');
            return;
        }

        try {
            await apiHelper.addArticleImage(articleId!, selectedFile, image);
            refreshPage();
        } catch (error) {
            setUploadStatus('Failed to upload image.');
        }
    };

    const refreshPage = async () => {
        setUploadStatus('Image uploaded successfully!');
        setSelectedFile(null);
        setImage({
            imageUrl: '',
            type: ImageType.ArticleImage, // Default value
            title: '',
            content: '',
            source: '',
            credit: ''
        });
        // Refresh the images list
        const data = await apiHelper.fetchArticleImages(articleId!);
        const mappedImages = data.images.map((img: any) => ({
            ...img,
            type: mapImageType(img.type)
        }));
        setImages(mappedImages);
    }

    const handleDelete = async (imageUrl: string) => {
        try {
            await apiHelper.deleteArticleImage(articleId!, imageUrl);
            // Refresh the images list
            const data = await apiHelper.fetchArticleImages(articleId!);
            const mappedImages = data.images.map((img: any) => ({
                ...img,
                type: mapImageType(img.type)
            }));
            setImages(mappedImages);
        } catch (error) {
            alert('Failed to delete image.');
        }
    };

    const handleEdit = (image: Image) => {
        setImage({
            imageUrl: image.imageUrl || '',
            type: image.type || ImageType.ArticleImage,
            title: image.title || '',
            content: image.content || '',
            source: image.source || '',
            credit: image.credit || ''
        });
    };

    return (
        <div className="manage-images-container">
            <h1>Manage Images for Article {articleId}</h1>
            <input type="file" onChange={handleFileChange} />
            <select
                name="type"
                value={image.type}
                onChange={handleInputChange}
            >
                <option value={ImageType.MainImage}>Main Image</option>
                <option value={ImageType.ArticleImage}>Article Image</option>
                <option value={ImageType.SlideShowImage}>Slide Show Image</option>
            </select>
            <input
                type="text"
                name="title"
                value={image.title}
                onChange={handleInputChange}
                placeholder="Title"
            />
            <input
                type="text"
                name="content"
                value={image.content}
                onChange={handleInputChange}
                placeholder="Content"
            />
            <input
                type="text"
                name="source"
                value={image.source}
                onChange={handleInputChange}
                placeholder="Source"
            />
            <input
                type="text"
                name="credit"
                value={image.credit}
                onChange={handleInputChange}
                placeholder="Credit"
            />
            <button onClick={handleUpload}>Upload</button>
            {uploadStatus && <p>{uploadStatus}</p>}
            <div className="images-list">
                {images.map(image => (
                    <div key={image.title} className="image-item">
                        <img src={image.imageUrl} alt={image.title} />
                        <div className="image-details">
                            <p><strong>Type:</strong> {image.type}</p>
                            <p><strong>Title:</strong> {image.title}</p>
                            <p><strong>Content:</strong> {image.content}</p>
                            <p><strong>Source:</strong> {image.source}</p>
                            <p><strong>Credit:</strong> {image.credit}</p>
                        </div>
                        <button className='edit-button' onClick={() => handleEdit(image)}>Edit</button>
                        <button className='delete-button' onClick={() => handleDelete(image.imageUrl)}>Delete</button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ManageImages;